import { shadows } from '@ardoq/design-tokens';
import { Box, Inline } from '@ardoq/layout';
import { isNodeSelection } from '@tiptap/core';
import { Editor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useCallback } from 'react';
import { ButtonSize, GhostButton, SecondaryButton } from '@ardoq/button';
import {
  FALLBACK_IMAGE_SIZE_PRESET,
  IMAGE_SIZE_PRESETS,
  ImageSizePreset,
  isImageSizePreset,
} from './imageSizing';

export type ImageBubbleMenuLabels = Record<ImageSizePreset, string> & {
  groupLabel: string;
};

export const defaultImageBubbleMenuLabels: ImageBubbleMenuLabels = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  groupLabel: 'Image size',
};

type ImageBubbleMenuProps = {
  editor: Editor | null;
  labels?: ImageBubbleMenuLabels;
};

const readSelectedSize = (editor: Editor | null): ImageSizePreset | null => {
  if (!editor) return null;
  const { selection } = editor.state;
  if (!isNodeSelection(selection) || selection.node.type.name !== 'image') {
    return null;
  }
  const { size } = selection.node.attrs;
  return isImageSizePreset(size) ? size : FALLBACK_IMAGE_SIZE_PRESET;
};

export const ImageBubbleMenu = ({
  editor,
  labels = defaultImageBubbleMenuLabels,
}: ImageBubbleMenuProps) => {
  const activePreset = useEditorState({
    editor,
    selector: ctx => readSelectedSize(ctx.editor),
  });

  const shouldShow = useCallback(
    ({ editor }: { editor: Editor }) =>
      editor.isEditable && readSelectedSize(editor) !== null,
    []
  );

  const applyPreset = (preset: ImageSizePreset) => {
    if (!editor) return;
    const position = editor.state.selection.from;
    // updateAttributes replaces the node, which collapses the node selection
    // and detaches this menu, so the image has to be re-selected afterwards.
    editor
      .chain()
      // Clearing the pixel dimensions keeps hand-authored =WxH content from
      // overriding the preset when the markdown is written back out.
      .updateAttributes('image', { size: preset, width: null, height: null })
      .setNodeSelection(position)
      .run();
  };

  return (
    <BubbleMenu
      editor={editor ?? undefined}
      shouldShow={shouldShow}
      options={{ placement: 'bottom-start' }}
    >
      <Box
        borderWidth={1}
        backgroundColor="surfaceDefault"
        borderRadius="small"
        padding="xsmall"
        style={{ boxShadow: shadows.shadowM }}
      >
        <Inline gap="xxsmall" role="group" aria-label={labels.groupLabel}>
          {IMAGE_SIZE_PRESETS.map(preset => {
            const Button =
              preset === activePreset ? SecondaryButton : GhostButton;
            return (
              <Button
                key={preset}
                buttonSize={ButtonSize.SMALL}
                aria-pressed={preset === activePreset}
                onMouseDown={event => event.preventDefault()}
                onClick={() => applyPreset(preset)}
              >
                {labels[preset]}
              </Button>
            );
          })}
        </Inline>
      </Box>
    </BubbleMenu>
  );
};
